import { Box, Report, UserOutlined, Workflows } from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import React from "react";
import { useTranslation } from "react-i18next";

import { AIGettingStartedButton } from "./getting-started-button/AIGettingStartedButton";
import { ErasureIcon } from "components/icons/ErasureIcon";
import { MagicWand } from "components/icons/MagicWand";
import { IBR_FEATURES } from "components/licenses/common";
import {
    AUTH_LICENSE_VIEW,
    AUTH_LICENSE_VIEW_OWN,
    AUTH_REPORT_VIEW,
    AUTH_WORKFLOW_CREATE,
    AUTH_WORKFLOW_VIEW,
} from "domain/authority";
import { toFeatureLicenseType } from "domain/users";
import { FLAG_AI_TENANT_STATE } from "services/feature/FeatureFlagService";
import { userSessionService } from "services/user/UserSessionService";

import testIds from "testIds.json";

interface Props {
    isTrialTenant: boolean;
    tenantHasReports: boolean;
    onSubmit: (query: string) => void;
}

interface Item {
    icon: React.ReactNode;
    text: string;
    testId: string;
    isAvailable?: boolean;
}

function getDefaultPromptItems(isTrialTenant: boolean, tenantHasReports: boolean): Item[] {
    const canViewReports = userSessionService.userHasAllAuthorities([AUTH_REPORT_VIEW]);
    const canViewOwnLicenses = userSessionService.userHasAllAuthorities([AUTH_LICENSE_VIEW_OWN]);
    const canViewLicenses = userSessionService.userHasAllAuthorities([AUTH_LICENSE_VIEW]);
    const canCreateWorkflows =
        userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_CREATE, AUTH_WORKFLOW_VIEW]) &&
        userSessionService.hasAnyFeatureLicense(IBR_FEATURES.map(toFeatureLicenseType));
    if (isTrialTenant && tenantHasReports) {
        return [
            {
                icon: <Report color={"--app-aiPanel-iconColor"} />,
                text: "AiAgent.defaultPrompts.trialWithReports.analyzeReport",
                testId: testIds.workArea.aiPanel.gettingStartedPanel.trialWithReportsAnalyzeReportButton,
                isAvailable: canViewReports,
            },
            {
                icon: <UserOutlined color={"--app-aiPanel-iconColor"} />,
                text: "AiAgent.defaultPrompts.trialWithReports.invite",
                testId: testIds.workArea.aiPanel.gettingStartedPanel.trialWithReportsInviteButton,
            },
            {
                icon: <Report color={"--app-aiPanel-iconColor"} />,
                text: "AiAgent.defaultPrompts.trialWithReports.reports",
                testId: testIds.workArea.aiPanel.gettingStartedPanel.trialWithReportsReportsButton,
                isAvailable: canViewReports,
            },
            {
                icon: <UserOutlined color={"--app-aiPanel-iconColor"} />,
                text: "AiAgent.defaultPrompts.trialWithReports.licenses",
                testId: testIds.workArea.aiPanel.gettingStartedPanel.trialWithReportsLicensesButton,
                isAvailable: canViewLicenses,
            },
        ];
    }
    if (isTrialTenant) {
        return [
            {
                icon: <ErasureIcon color={"--app-aiPanel-iconColor"} size={20} />,
                text: "AiAgent.defaultPrompts.trialWithoutReports.erase",
                testId: testIds.workArea.aiPanel.gettingStartedPanel.trialWithoutReportsEraseButton,
            },
            {
                icon: <Workflows color={"--app-aiPanel-iconColor"} />,
                text: "AiAgent.defaultPrompts.trialWithoutReports.workflow",
                testId: testIds.workArea.aiPanel.gettingStartedPanel.trialWithoutReportsWorkflowButton,
                isAvailable: canCreateWorkflows,
            },
            {
                icon: <UserOutlined color={"--app-aiPanel-iconColor"} />,
                text: "AiAgent.defaultPrompts.trialWithoutReports.licensing",
                testId: testIds.workArea.aiPanel.gettingStartedPanel.trialWithoutReportsLicensingButton,
            },
        ];
    }
    if (tenantHasReports) {
        return [
            {
                icon: <MagicWand color={"--app-aiPanel-iconColor"} />,
                text: "AiAgent.defaultPrompts.productionWithReports.devices",
                testId: testIds.workArea.aiPanel.gettingStartedPanel.productionWithReportsDevicesButton,
                isAvailable: canViewReports,
            },
            {
                icon: <Report color={"--app-aiPanel-iconColor"} />,
                text: "AiAgent.defaultPrompts.productionWithReports.report",
                testId: testIds.workArea.aiPanel.gettingStartedPanel.productionWithReportsReportButton,
                isAvailable: canViewReports,
            },
            {
                icon: <UserOutlined color={"--app-aiPanel-iconColor"} />,
                text: "AiAgent.defaultPrompts.productionWithReports.licenses",
                testId: testIds.workArea.aiPanel.gettingStartedPanel.productionWithReportsLicensesButton,
                isAvailable: canViewOwnLicenses,
            },
        ];
    }
    //TODO BCC-6780: Update this production tenant with no reports state with actual prompts once known.
    return [
        {
            icon: <ErasureIcon color={"--app-aiPanel-iconColor"} size={20} />,
            text: "AiAgent.defaultPrompts.productionWithoutReports.devices",
            testId: testIds.workArea.aiPanel.gettingStartedPanel.productionWithoutReportsDevicesButton,
        },
        {
            icon: <UserOutlined color={"--app-aiPanel-iconColor"} />,
            text: "AiAgent.defaultPrompts.productionWithoutReports.licenses",
            testId: testIds.workArea.aiPanel.gettingStartedPanel.productionWithoutReportsLicensesButton,
        },
    ];
}

export const AIGettingStartedPanel = (props: Props) => {
    const { t } = useTranslation();

    // TODO: Render buttons based on user's role and usage patterns instead of authorities.
    const hasWorkflowAuths = userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_CREATE, AUTH_WORKFLOW_VIEW]);
    const hasReportAuths = userSessionService.userHasAllAuthorities([AUTH_REPORT_VIEW]);
    const userHasAnyWorkflowLicense = userSessionService.hasAnyFeatureLicense(IBR_FEATURES.map(toFeatureLicenseType));

    const items: Item[] = useFeature(FLAG_AI_TENANT_STATE)
        ? getDefaultPromptItems(props.isTrialTenant, props.tenantHasReports)
        : [
              {
                  icon: <MagicWand color={"--app-aiPanel-iconColor"} />,
                  text: "AiAgent.gettingStarted.chooseProduct",
                  testId: testIds.workArea.aiPanel.gettingStartedPanel.chooseProductButton,
              },
              {
                  icon: <Workflows color={"--app-aiPanel-iconColor"} />,
                  testId:
                      hasWorkflowAuths && userHasAnyWorkflowLicense
                          ? testIds.workArea.aiPanel.gettingStartedPanel.setUpWorkflowButton
                          : testIds.workArea.aiPanel.gettingStartedPanel.explainWorkflowsButton,
                  text:
                      hasWorkflowAuths && userHasAnyWorkflowLicense
                          ? "AiAgent.gettingStarted.setUpWorkflow"
                          : "AiAgent.gettingStarted.explainWorkflows",
              },
              {
                  icon: <UserOutlined color={"--app-aiPanel-iconColor"} />,
                  text: "AiAgent.gettingStarted.checkLicenseStatus",
                  testId: testIds.workArea.aiPanel.gettingStartedPanel.checkLicenseStatusButton,
              },
              {
                  icon: <Report color={"--app-aiPanel-iconColor"} />,
                  text: hasReportAuths ? "AiAgent.gettingStarted.viewReports" : "AiAgent.gettingStarted.explainReports",
                  testId: hasReportAuths
                      ? testIds.workArea.aiPanel.gettingStartedPanel.viewReportsButton
                      : testIds.workArea.aiPanel.gettingStartedPanel.explainReportsButton,
              },
          ];

    return (
        <Box
            flexWrap={"Wrap"}
            gap={"Sm"}
            horizontalAlignment="Center"
            testId={testIds.workArea.aiPanel.gettingStartedPanel.itself}
        >
            {items
                .filter((item) => item.isAvailable ?? true)
                .map((item) => (
                    <AIGettingStartedButton
                        key={item.testId}
                        onClick={() => props.onSubmit(t(item.text))}
                        testId={item.testId}
                        icon={item.icon}
                    >
                        {t(item.text)}
                    </AIGettingStartedButton>
                ))}
        </Box>
    );
};
